import {
  Alert,
  AlertIcon,
  Box,
  Button,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  BrandRevenueGroupByEnum,
  BrandRevenueWhereColumn,
  useGetProjectCategoriesQuery,
} from "@src/__generated__/urql-graphql";
import { DataTable } from "@src/components/ui-kit";
import { PeriodPicker } from "@src/components/widgets/PeriodPicker";
import { FiltersPanelRoot } from "@src/utils/components/filters/components/filters-panel";
import { FiltersPanelFilters } from "@src/utils/components/filters/components/filters-panel-filters";
import { FiltersPanelSearch } from "@src/utils/components/filters/components/filters-panel-search";
import { upperFirst } from "lodash";
import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import type { BrandReportStore } from "./store";

type Props = {
  store: BrandReportStore;
};

export const TableNavbar = observer(function TableNavbar({ store }: Props) {
  const [{ data }] = useGetProjectCategoriesQuery();

  useEffect(() => {
    if (!data?.projectCategories) return;
    store.where.filtersByColumn
      .get(BrandRevenueWhereColumn.CategoryId)
      ?.setOptions(
        data.projectCategories.map(({ id, name }) => ({
          value: id,
          label: name,
        })),
      );
  }, [data?.projectCategories]);

  return (
    <DataTable.NavBar>
      <Box py="4">
        <FiltersPanelRoot filters={store.where}>
          <VStack align="start" w="full" spacing="4">
            <HStack w="full" spacing="2">
              <PeriodPicker
                topExtraContent={
                  <Box px="4" pt="4">
                    <Alert borderRadius="md" status="info">
                      <AlertIcon />
                      <Text>
                        <Trans>
                          Show financial data from this selected date range,
                          based on "Date of supply"
                        </Trans>
                      </Text>
                    </Alert>
                  </Box>
                }
                customActions={store.periodPickerActions}
                onChange={store.setDateRange}
                value={store.dateRange}
              />
              <Menu>
                <MenuButton
                  as={Button}
                  minW="fit-content"
                  colorScheme="grey"
                  variant="outline"
                >
                  <Trans>Group by:</Trans>&nbsp;
                  {upperFirst(store.groupBy.toLowerCase())}
                </MenuButton>
                <MenuList>
                  <MenuItem
                    onClick={() => {
                      store.setGroupBy(BrandRevenueGroupByEnum.None);
                    }}
                  >
                    <Trans>None</Trans>
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      store.setGroupBy(BrandRevenueGroupByEnum.Client);
                    }}
                  >
                    <Trans>Client</Trans>
                  </MenuItem>
                </MenuList>
              </Menu>
              <FiltersPanelSearch
                w="full"
                inputWidth="full"
                searchPlaceholder={t`Search by brand, code, project`}
                defaultValue={store.searchTerm}
                onSearch={(val) => {
                  store.searchTerm = val;
                  store.pagination.resetPage();
                }}
              />
              <FiltersPanelFilters
                menuPlacement="bottom-start"
                innerMenuPlacement="right-start"
              />
            </HStack>
          </VStack>
        </FiltersPanelRoot>
      </Box>
    </DataTable.NavBar>
  );
});
